import { ArrowRightIcon, Button, Table, TableColumns } from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useSelectedSchoolYear } from '../../../hooks/useSelectedSchoolYear';
import { useCurriculum } from '../hooks/useCurriculum';

type CurriculumGradesTableType = {
  grade: number | string;
  name: string;
  subjectHours: string;
  studentHours: string;
  deputat: string;
};

type CurriculumGradesTableProps = {
  curriculumUuid: string | undefined;
  onNavigate: (grade: number | string) => void;
};

export const CurriculumGradesTable = observer(({ curriculumUuid, onNavigate }: CurriculumGradesTableProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const currentSchoolYear = useSelectedSchoolYear();

  const { tableData: allGradesData } = useCurriculum({ versionUuid: curriculumUuid ?? '' });

  const data: CurriculumGradesTableType[] = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, '-'].map((grade) => {
    return {
      grade,
      name: grade.toString(),
      subjectHours: allGradesData
        .map((d) => d.curriculumInfo?.classInfos.get('uuid_' + grade)?.studentCards ?? 0.0)
        .reduce((a, b) => a + b, 0.0)
        .toFixed(2),
      studentHours: allGradesData
        .map((d) => d.curriculumInfo?.classInfos.get('uuid_' + grade)?.studentHours ?? 0.0)
        .reduce((a, b) => a + b, 0.0)
        .toFixed(2),
      deputat: allGradesData
        .map((d) => d.curriculumInfo?.classInfos.get('uuid_' + grade)?.deputat ?? 0.0)
        .reduce((a, b) => a + b, 0.0)
        .toFixed(2),
    };
  });

  const columns: TableColumns<CurriculumGradesTableType>[] = [
    {
      header: t('classes.level.full'),
      id: 'name',
      accessorKey: 'name',
      size: 400,
    },
    {
      header: t('subjectHours.title', { count: 2 }),
      id: 'subjectHours',
      accessorKey: 'subjectHours',
      size: 200,
    },
    {
      header: t('subjectHours.studentHours'),
      accessorKey: 'studentHours',
      id: 'studentHours',
      size: 200,
    },
    {
      header: t('deputate.title', { count: 1 }),
      accessorKey: 'deputat',
      id: 'deputat',
      size: 200,
    },
  ];

  return (
    <Table<CurriculumGradesTableType>
      data={data}
      columns={columns}
      showRowHover
      showBorderRadius
      showShadow
      isOnWhite={false}
      showActionBar
      actionBarSettings={{
        showPrintButton: true,
      }}
      printerSettings={{
        headline: pimAuthClaims.getProfile()?.organization.name,
        subline: `${t('curriculum.title', { count: 2 })} - ${t('grades.title', { count: 2 })} - ${t('common.schoolYear')} ${currentSchoolYear?.shortName}`,
        filename: `${t('curriculum.title', { count: 2 })}_${currentSchoolYear?.shortName}`,
      }}
      onRowClick={(_e, row) => onNavigate(row.original.grade)}
      lastCol={(row) => {
        return (
          <Button
            hierarchy='ghost'
            onClick={() => onNavigate(row.original.grade)}
            icon={<ArrowRightIcon className='small' />}
          />
        );
      }}
    />
  );
});
